import SysColumn from "app/class/sys-column";
import Translate from "component/global/translation/translation-component";
import { useFormContext } from "react-hook-form";
import { ConditionBuilderField } from "./record-producer-fields/ConditionBuilderField";
import { TextFieldComponent } from "./record-producer-fields/TextField";
import { NumberFieldComponent } from "./record-producer-fields/NumberField";
import { CurrencyFieldComponent } from "./record-producer-fields/CurrencyField";
import { BooleanFieldComponent } from "./record-producer-fields/BooleanField";
import { DateFieldComponent } from "./record-producer-fields/DateField";
import { DatetimeFieldComponent } from "./record-producer-fields/DatetimeField";
import { TimeFieldComponent } from "./record-producer-fields/TimeField";
import { ReferenceFieldComponent } from "./record-producer-fields/ReferenceField";
import { ChoiceFieldComponent } from "./record-producer-fields/ChoiceField";
import { CodeFieldComponent } from "./record-producer-fields/CodeField";
import { RichHtmlFieldComponent } from "./record-producer-fields/RichHtmlField";
import { AttachmentFieldComponent } from "./record-producer-fields/AttachmentField";
import { PasswordFieldComponent } from "./record-producer-fields/PasswordField";
import { TranslatableTextFieldComponent } from "./record-producer-fields/TranslatableTextField";
import { ColorPickerFieldComponent } from "./record-producer-fields/ColorPickerField";
import { FieldRuntimeProps } from "../types/record-producer-types";
import { PermissionArrayField } from "./record-producer-fields/PermissionArrayField";
import { TableNameFieldComponent } from "./record-producer-fields/TableNameField";
import { useRecordProducer } from "./RecordProducerContext";
import { resolveRecordProducerFieldType } from "../services/resolveRecordProducerFieldType";
import { shouldRenderProducerField } from "../services/recordProducerFieldVisibility";

type FieldFactoryProps = {
    column: SysColumn;
    runtimeProps?: FieldRuntimeProps;
};

export function FieldFactory({ column, runtimeProps }: FieldFactoryProps) {
    const { control } = useFormContext();
    const recordProducer = useRecordProducer();
    if (!shouldRenderProducerField(recordProducer.table.name, recordProducer.mode, column.name)) {
        return null;
    }
    const fieldType = resolveRecordProducerFieldType({ table: recordProducer.table, column });

    switch (fieldType) {
        case "PERMISSION_ARRAY":
            return <PermissionArrayField column={column} runtimeProps={runtimeProps} />;
        case "TABLE_NAME":
            return <TableNameFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;
        case "CONDITION": // 
            return <ConditionBuilderField column={column} control={control} runtimeProps={runtimeProps} />
        case "TEXT":
        case "UUID":
            return <TextFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;

        case "PASSWORD":
            return <PasswordFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;

        case "TRANSLATABLE_TEXT":
            return <TranslatableTextFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;

        case "COLOR_PICKER":
            return <ColorPickerFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;

        case "INTEGER":
        case "DECIMAL":
            return <NumberFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;

        case "CURRENCY":
            return <CurrencyFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;

        case "BOOLEAN":
            return <BooleanFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;

        case "DATE":
            return <DateFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;
        case "DATETIME":
            return <DatetimeFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;
        case "TIME":
            return <TimeFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;

        case "REFERENCE":
        case "DYNAMIC_REFERENCE":
            return <ReferenceFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;

        case "CHOICE":
            return <ChoiceFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;

        case "CODE":
            return <CodeFieldComponent column={column} runtimeProps={runtimeProps} />; //  control={control}

        case "RICH_HTML":
            return <RichHtmlFieldComponent column={column} control={control} runtimeProps={runtimeProps} />;

        case "ATTACHMENT":
            return <AttachmentFieldComponent column={column} runtimeProps={runtimeProps} />;

        default:
            return <Translate text={`Field type '${fieldType}' is not supported yet.`} />;
    }
};
